import { useMemo } from 'react';

import { getDisabledEditButtonOptions } from 'features/employeeProfile/PersonalInformationView/ContactInformation/utils';

import { postMergeRequestPayrollInformationEditButtonAttrs } from 'util/postMergeRequestPayrollInformationEditButtonAttrs';
import { editButtonAttrs } from 'util/profile';

import { postMergeRequestBankingInformationEditButtonAttrs } from './postMergeRequestBankingInformationEditButtonAttrs';

interface UserData {
  has_jobs_at_more_than_one_company: boolean;
  id: string;
  first_name: string;
  enabled_bank_account_edit_button: boolean;
  is_merge_user_active: boolean;
  is_temp_user: boolean;
  has_been_paid_in_last_30_days: boolean;
}

type UserKey = keyof UserData | 'mfa_enabled';

interface UserRecord {
  get<T>(key: UserKey): T | undefined;
}
interface PersonalInformationEditStateParams {
  isRolloutEnabled: boolean;
  user: UserRecord;
  currentUserId: number;
  bankAccountsOpsEnabled?: boolean;
  isPayrollEnrolled?: boolean;
}

interface PersonalInformationEditState {
  disableEdits: boolean;
  showBankingInformation: boolean;
  hiddenContactInformationEditButton?: boolean;
  contactInformationEditButtonText?: string;
  drawerContactInformationEditButtonDisabled?: boolean;
  showDrawerContactInformationAlert?: boolean;
  showEmployeeWillBeNotifiedAlert?: boolean;
  showDrawerEmployeeInformationAlert?: boolean;
  employeeInformationEditButton?: {
    disabled: boolean;
    content: string;
  };
  showOnlyEmployeeWillBeNotifiedForPaidIn30DaysAlert?: boolean;
  employeeBankingInformationEditButton?: {
    disabled: boolean;
    content: string;
  };
}

const defaultState = {
  disableEdits: false,
  showBankingInformation: true,
  hiddenContactInformationEditButton: false,
  contactInformationEditButtonText: '',
  drawerContactInformationEditButtonDisabled: true,
  showDrawerContactInformationAlert: false,
  showEmployeeWillBeNotifiedAlert: false,
  showDrawerEmployeeInformationAlert: false,
  employeeInformationEditButton: {
    disabled: false,
    content: '',
  },
  showOnlyEmployeeWillBeNotifiedForPaidIn30DaysAlert: false,
  employeeBankingInformationEditButton: {
    disabled: false,
    content: '',
  },
};

/**
 * Hook to determine the edit state for personal information widget and dashboard drawer.
 *
 * This hook handles different scenarios:
 * 1. Rollout disabled: Maintains current behavior
 * 2. Rollout enabled + merge request pending: Enables all edits without any alerts
 * 3. Rollout enabled + paid in last 30 days + multiple companies:
 *    - Disables Contact Information and Payroll Information edit buttons with alert,
 *    - Enables Banking Information edit button with alert
 * 4. Rollout enabled + not paid in last 30 days:
 *    - Disables Contact Information edit button with alert,
 *    - Enables Payroll Information and Banking Information edit buttons with alerts
 *
 */

export const usePersonalInformationEditState = ({
  isRolloutEnabled,
  user,
  currentUserId,
  bankAccountsOpsEnabled = false,
  isPayrollEnrolled = false,
}: PersonalInformationEditStateParams): PersonalInformationEditState =>
  useMemo(() => {
    if (!user || typeof (user as any).get !== 'function') {
      return defaultState;
    }
    const contactInformationEditButtonOptions = getDisabledEditButtonOptions(
      user,
      currentUserId
    );
    const hasJobsAtMoreThanOneCompany = Boolean(
      user.get<boolean>('has_jobs_at_more_than_one_company')
    );
    const userId = Number(user.get<number>('id') ?? 0);
    const userFullName = String(user.get<string>('first_name') ?? '');
    const enabledBankAccountEditButton = Boolean(
      user.get<boolean>('enabled_bank_account_edit_button')
    );
    const isMergeUserActive = Boolean(
      user.get<boolean>('is_merge_user_active')
    );
    const isTempUser = Boolean(user.get<boolean>('is_temp_user'));
    const hasBeenPaidInLast30Days = Boolean(
      user.get<boolean>('has_been_paid_in_last_30_days')
    );

    const isMergeRequestPending = isTempUser && isMergeUserActive;
    const isUserProfileOwner = userId === currentUserId;
    const currentEmployeeInformationProfile = {
      userId,
      userFullName,
      moreThanOneCompany: hasJobsAtMoreThanOneCompany,
      informationType: 'personal',
    };

    const employeeInformationEditButton = editButtonAttrs(
      currentEmployeeInformationProfile,
      currentUserId
    );
    const postMergeRequestEmployeeInformationEditButton =
      postMergeRequestPayrollInformationEditButtonAttrs(
        currentEmployeeInformationProfile,
        currentUserId,
        hasBeenPaidInLast30Days
      );

    const currentBankingInformationProfile = {
      userId,
      userFullName,
      informationType: 'banking',
      disableBankAccountsOps:
        enabledBankAccountEditButton && !bankAccountsOpsEnabled,
      moreThanOneCompany: hasJobsAtMoreThanOneCompany,
      disableBankAccountEditButton: !enabledBankAccountEditButton,
    };

    const employeeBankingInformationEditButton = editButtonAttrs(
      currentBankingInformationProfile,
      currentUserId,
      isPayrollEnrolled
    );
    const postMergeRequestEmployeeBankingInformationEditButton =
      postMergeRequestBankingInformationEditButtonAttrs(
        currentBankingInformationProfile,
        currentUserId,
        isPayrollEnrolled
      );

    const hasJobsAtMoreThanOneCompanyAndIsNotUserProfileOwner =
      isUserProfileOwner ? false : hasJobsAtMoreThanOneCompany;

    // If rollout is disabled, maintain current behavior
    if (!isRolloutEnabled) {
      return {
        disableEdits: isMergeRequestPending,

        showBankingInformation: !isMergeRequestPending,

        hiddenContactInformationEditButton: Boolean(
          contactInformationEditButtonOptions
        ),
        contactInformationEditButtonText: contactInformationEditButtonOptions,
        drawerContactInformationEditButtonDisabled: hasJobsAtMoreThanOneCompany,
        showDrawerContactInformationAlert: false,
        showDrawerEmployeeInformationAlert: hasJobsAtMoreThanOneCompany,
        showEmployeeWillBeNotifiedAlert: false,
        employeeInformationEditButton,
        showOnlyEmployeeWillBeNotifiedForPaidIn30DaysAlert: false,
        employeeBankingInformationEditButton,
      };
    }

    // If rollout is enabled and merge request is pending
    if (isMergeRequestPending) {
      return {
        disableEdits: false,
        showBankingInformation: isPayrollEnrolled,
        hiddenContactInformationEditButton: false,
        contactInformationEditButtonText: '',
        drawerContactInformationEditButtonDisabled: false,
        showDrawerContactInformationAlert: false,
        showDrawerEmployeeInformationAlert: false,
        showEmployeeWillBeNotifiedAlert: false,
        employeeInformationEditButton,
        showOnlyEmployeeWillBeNotifiedForPaidIn30DaysAlert: false,
        employeeBankingInformationEditButton,
      };
    }

    // If rollout is enabled, no merge request pending, and has been paid in last 30 days
    if (hasBeenPaidInLast30Days && hasJobsAtMoreThanOneCompany) {
      return {
        disableEdits: !isUserProfileOwner,
        showBankingInformation: isPayrollEnrolled,
        hiddenContactInformationEditButton: isUserProfileOwner
          ? false
          : Boolean(contactInformationEditButtonOptions),
        contactInformationEditButtonText: isUserProfileOwner
          ? ''
          : contactInformationEditButtonOptions,
        drawerContactInformationEditButtonDisabled:
          hasJobsAtMoreThanOneCompanyAndIsNotUserProfileOwner,
        showDrawerContactInformationAlert:
          hasJobsAtMoreThanOneCompanyAndIsNotUserProfileOwner,
        showDrawerEmployeeInformationAlert: false,
        showEmployeeWillBeNotifiedAlert: false,
        employeeInformationEditButton:
          postMergeRequestEmployeeInformationEditButton,
        showOnlyEmployeeWillBeNotifiedForPaidIn30DaysAlert: !isUserProfileOwner,
        employeeBankingInformationEditButton:
          postMergeRequestEmployeeBankingInformationEditButton,
      };
    }

    // If rollout is enabled, no merge request pending, and has NOT been paid in last 30 days
    return {
      disableEdits: false,
      showBankingInformation: isPayrollEnrolled,
      hiddenContactInformationEditButton: isUserProfileOwner
        ? false
        : Boolean(contactInformationEditButtonOptions),
      contactInformationEditButtonText: isUserProfileOwner
        ? ''
        : contactInformationEditButtonOptions,
      drawerContactInformationEditButtonDisabled:
        hasJobsAtMoreThanOneCompanyAndIsNotUserProfileOwner,
      showDrawerContactInformationAlert:
        hasJobsAtMoreThanOneCompanyAndIsNotUserProfileOwner,
      showDrawerEmployeeInformationAlert:
        hasJobsAtMoreThanOneCompanyAndIsNotUserProfileOwner,
      showEmployeeWillBeNotifiedAlert:
        hasJobsAtMoreThanOneCompanyAndIsNotUserProfileOwner,
      employeeInformationEditButton:
        postMergeRequestEmployeeInformationEditButton,
      showOnlyEmployeeWillBeNotifiedForPaidIn30DaysAlert: false,
      employeeBankingInformationEditButton:
        postMergeRequestEmployeeBankingInformationEditButton,
    };
  }, [
    isRolloutEnabled,
    user,
    currentUserId,
    bankAccountsOpsEnabled,
    isPayrollEnrolled,
  ]);
